import {
  Toolbar,
  Annotation,
  Navigation,
  Magnification,
  ThumbnailView,
} from "@syncfusion/ej2-react-pdfviewer";
import React from "react";
import { Inject, PdfViewerComponent } from "@syncfusion/ej2-react-pdfviewer";
import { usePdfTemplateDesignerComponent } from "./usePdfTemplateDesignerComponent";
import classNames from "classnames";
import { PdfTemplateDesignerHeaderToolbar } from "./PdfTemplateDesignerHeaderToolbar";
import { PdfTemplateDesignerExpressionsToolbar } from "./PdfTemplateDesignerExpressionsToolbar";
import { PdfTemplateDesignerPreviewToolbar } from "./PdfTemplateDesignerPreviewToolbar";
import { MessageComponent } from "@syncfusion/ej2-react-notifications";
import { ExpressionData, AnnotationDefaults } from "../types/types";
import { LoadingV2 } from "../../Loading/LoadingV2";

export interface FileDocument {
  fileName: string;
  folderName: string;
  isChunked: boolean;
}

export interface PdfTemplateDesignerProps {
  id?: string;
  serviceUrl: string;
  pdfViewerRef: React.RefObject<PdfViewerComponent | null>;
  fileFormData?: string;
  fileDocument?: string;
  onSave?: (fileFormData?: string) => Promise<void>;
  apiBaseUrl?: string;
  zoomValue?: number;
  getTemplateFields?: () => Promise<ExpressionData[]>;
  getTemplateFieldsData?: (
    formId?: string
  ) => Promise<Record<string, string> | {}>;
  previewBaseODataUrl?: string;
  previewODataKey?: string;
  previewGetAuthRequestHeader?: () => Promise<
    Record<string, string> | undefined
  >;
  onPreviewAnnotations?: (data?: any, annotationsData?: string) => void;
  designerHeaderContent?: React.ReactNode;
  annotationDefaults?: AnnotationDefaults;
}

export const PdfTemplateDesigner: React.FC<PdfTemplateDesignerProps> = (
  props
) => {
  const {
    defaultPdfViewerSettings,
    pdfViewerSettings,
    isLoading,
    templateDesignerModule,
    isPreview,
    setIsPreview,
    isReadOnlyMode,
    notSupported,
    ready,
  } = usePdfTemplateDesignerComponent(props);

  return (
    <section className="ucl-pdf-template-designer-container">
      {isLoading && (
        <div className="template-loading">
          <LoadingV2 loadingType="spinner" />
        </div>
      )}
      <div
        className={classNames("ucl-pdfviewer-component-container", {
          "ucl-pdfviewer-component-container-loading": isLoading,
          "is-preview": isPreview,
          "is-readonly-mode": isReadOnlyMode,
        })}
      >
        {ready && (
          <>
            {notSupported && (
              <MessageComponent title="Not Supported" severity="warning">
                The PDF File is locked, protected, or not supported by the
                system.
              </MessageComponent>
            )}
            {isReadOnlyMode && (
              <MessageComponent title="Read Only" severity="primary">
                The PDF File is loaded on read only mode.
              </MessageComponent>
            )}
            <PdfTemplateDesignerHeaderToolbar
              allowPreview={
                (!!props.previewBaseODataUrl &&
                  !!props.previewODataKey &&
                  !!props.previewGetAuthRequestHeader) ||
                !!props.getTemplateFieldsData
              }
              onPreviewToggle={setIsPreview}
              hideFieldsTab={isReadOnlyMode}
              templateDesignerModule={templateDesignerModule}
              designerHeaderContent={props.designerHeaderContent}
            />
            <div
              className={`template-designer-controls ${
                props.id ? `${props.id}` : "ucl-pdf-template-designer-component"
              }`}
            >
              <PdfViewerComponent
                ref={props.pdfViewerRef}
                className="e-control e-lib e-pdfviewer"
                serviceUrl={props.serviceUrl}
                disabled={
                  isReadOnlyMode || templateDesignerModule?.isSaving || false
                }
                enableAnnotation={!templateDesignerModule?.isSaving}
                {...defaultPdfViewerSettings}
                {...pdfViewerSettings}
                documentPath={props.fileDocument}
                zoomMode="Default"
                zoomValue={props.zoomValue || 50}
              >
                <Inject
                  services={[
                    Annotation,
                    Toolbar,
                    ThumbnailView,
                    Navigation,
                    Magnification,
                  ]}
                />
              </PdfViewerComponent>
              {!isReadOnlyMode && (
                <>
                  {!isPreview && (
                    <PdfTemplateDesignerExpressionsToolbar
                      disableFields={
                        isPreview || templateDesignerModule?.isSaving || false
                      }
                      getTemplateFields={props.getTemplateFields}
                      templateDesignerModule={templateDesignerModule}
                      pdfViewerRef={props.pdfViewerRef}
                      annotationDefaults={props.annotationDefaults}
                    />
                  )}
                  {isPreview &&
                    ((!!props.previewBaseODataUrl &&
                      !!props.previewODataKey &&
                      !!props.previewGetAuthRequestHeader) ||
                      props.getTemplateFieldsData) && (
                      <PdfTemplateDesignerPreviewToolbar
                        baseODataUrl={props.previewBaseODataUrl}
                        oDataKey={props.previewODataKey}
                        getAuthRequestHeader={props.previewGetAuthRequestHeader}
                        templateDesignerModule={templateDesignerModule}
                        getTemplateFieldsData={props.getTemplateFieldsData}
                      />
                    )}
                </>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PdfTemplateDesigner;
